import { useCallback, useMemo } from "react";
import useTypedSearchParams from "@/features/invoices/hooks/use-typed-search-params";
import {
  PathPickBasketRequestStatus,
  PathPickBasketSoldOutFilter,
  PathPickBasketSortOption,
  PathPickBasketTab,
} from "@/features/pathpick/features/basket/types";
import {
  BASKET_COMPARISON_SORT_OPTIONS,
  BASKET_MARGIN_SORT_OPTIONS,
  BASKET_SORT_OPTIONS_BY_TAB,
} from "@/features/pathpick/features/basket/constants";
import {
  PathPickMarketPriceBasis,
  PathPickProductRequestKind,
  PathPickProductType,
} from "@/features/pathpick/types";
import usePathPickProductCardView from "@/features/pathpick/hooks/usePathPickProductCardView";
import {
  CLEARED_PATH_PICK_RANGE_FILTER,
  hasPathPickRangeFilter,
  parsePathPickRangeFilter,
  PATH_PICK_RANGE_FILTER_KEYS,
  PathPickRangeFilter,
} from "@/features/pathpick/utils/range-filter";

// 범위 필터(마진·거래수)는 상품 찾기와 같은 키로 쿼리스트링에 둔다 — 모든 탭 공통이다.
export interface PathPickBasketFilter extends PathPickRangeFilter {
  query?: string;
  // 상태 탭 — 디폴트("pending")는 쿼리스트링에서 생략한다.
  tab?: PathPickBasketTab;
  pageNumber?: number;
  pageSize?: number;
  // 브랜드/카테고리 — 상품 조회 API가 그대로 지원하는 필터
  brandId__in?: number[];
  pathradeCategory__in?: string[];
  // 상품 유형(작업 전 탭) — 단일 값 파라미터라 정확히 하나 선택했을 때만 요청에 포함된다.
  productType__in?: PathPickProductType[];
  // 처리 상태(등록 신청한 탭) — 서버 파라미터가 없어 현재는 화면 전용이다.
  // (체크/칩/전체 해제는 동작하지만 목록 요청에는 반영되지 않는다)
  // TODO(BE): 상품 조회에 request_status__in이 신설되면 요청에 연결한다.
  requestStatus__in?: PathPickBasketRequestStatus[];
  // 등록 채널(등록 신청한 탭) — request_type__in으로 목록 요청에 반영된다.
  requestChannel__in?: PathPickProductRequestKind[];
  // 품절 상품(전체 탭) — 단일 선택이라 배열이 아닌 스칼라이고, 전체(미적용)는 undefined다.
  // is_sold_out으로 목록 요청에 반영된다.
  soldOut?: PathPickBasketSoldOutFilter;
  // 정렬 — order_by로 목록 요청에 반영된다.
  sort?: PathPickBasketSortOption;
  // 비교 기준(크림·포이즌 공통) — market_price_basis로 목록 요청에 반영된다(마진 정렬의 계산 기준).
  // 담은 상품 목록은 헤더/그리드/액션 패널 등 여러 곳에서 같은 조회 훅을 인자 없이 호출하므로,
  // 로컬 state로 두면 react-query 캐시가 갈라진다 — 쿼리스트링으로 관리해 한 값을 공유한다.
  marketPriceBasis?: PathPickMarketPriceBasis;
}

export type PathPickBasketFilterKey = keyof PathPickBasketFilter;

// 필터 패널의 그룹 키 — 칩으로 노출되는 항목이자 `전체 해제`/탭 전환 시 초기화 대상이다.
// soldOut만 단일 선택(스칼라)이고 나머지는 배열이다.
const BASKET_GROUP_FILTER_KEYS: PathPickBasketFilterKey[] = [
  "brandId__in",
  "pathradeCategory__in",
  "productType__in",
  "requestStatus__in",
  "requestChannel__in",
  "soldOut",
];

// `전체 해제`/탭 전환 시 함께 비우는 키 — 그룹 필터 + 범위 필터.
const CLEARABLE_FILTER_KEYS: PathPickBasketFilterKey[] = [
  ...BASKET_GROUP_FILTER_KEYS,
  ...PATH_PICK_RANGE_FILTER_KEYS,
];

const FILTER_KEYS: PathPickBasketFilterKey[] = [
  "query",
  "tab",
  "pageNumber",
  "pageSize",
  ...CLEARABLE_FILTER_KEYS,
  "sort",
  "marketPriceBasis",
];

const TABS: PathPickBasketTab[] = ["pending", "requested", "ordered"];
const MARKET_PRICE_BASES: PathPickMarketPriceBasis[] = [
  "bidPrice",
  "tradePrice",
];
const DEFAULT_TAB: PathPickBasketTab = "pending";
const DEFAULT_PAGE_NUMBER = 1;
const DEFAULT_PAGE_SIZE = 100;
const DEFAULT_MARKET_PRICE_BASIS: PathPickMarketPriceBasis = "bidPrice";

// 관리하는 모든 필터 키를 항상 포함(빈 값은 undefined)시켜 쿼리스트링 관리 키를 일관되게 유지한다.
function normalize(filter: PathPickBasketFilter): PathPickBasketFilter {
  return FILTER_KEYS.reduce((acc, key) => {
    const value = filter[key];
    if (typeof value === "boolean") {
      // boolean 필터는 true일 때만 쿼리스트링에 남긴다(false는 미적용과 동일).
      acc[key] = (value || undefined) as any;
    } else if (typeof value === "number") {
      acc[key] = value as any;
    } else {
      acc[key] = value && value.length > 0 ? (value as any) : undefined;
    }
    return acc;
  }, {} as PathPickBasketFilter);
}

// 그룹·범위 필터 키를 모두 비운 값 — 전체 해제/탭 전환이 공유한다.
const CLEARED_GROUP_FILTERS = CLEARABLE_FILTER_KEYS.reduce(
  (acc, key) => ({ ...acc, [key]: undefined }),
  {} as PathPickBasketFilter
);

// 담은 상품 목록의 검색/탭/필터/정렬/페이지네이션 상태 — 새로고침에도 유지되도록 쿼리스트링으로 관리한다.
const usePathPickBasketFilter = () => {
  const [rawFilter, setFilter] = useTypedSearchParams<PathPickBasketFilter>();
  // 비교 표시를 끄면 마진·거래 정렬이 화면에서 사라진다 — URL에 남아 있어도 고를 수 없는
  // 조건이라 아래 sortOptions에서 함께 빼고, 값도 그 탭의 디폴트로 떨어진다.
  const { isComparisonVisible } = usePathPickProductCardView();

  // useTypedSearchParams는 값이 2개 이상인 숫자형 __in을 string[]로 파싱하는 한계가 있어
  // (공용 훅은 건드리지 않고) 여기서 brandId__in을 number[]로 정규화해 선언 타입 불변식을 복원한다.
  // 범위 값도 문자열로 들어오므로 숫자로 정규화하고, 크림·포이즌 비교 값으로 거르는 조건이라
  // 비교 표시를 끄면 정렬처럼 함께 떨어뜨린다.
  const filter = useMemo<PathPickBasketFilter>(
    () => ({
      ...rawFilter,
      ...(isComparisonVisible
        ? parsePathPickRangeFilter(rawFilter)
        : CLEARED_PATH_PICK_RANGE_FILTER),
      brandId__in: rawFilter.brandId__in?.map(Number),
    }),
    [rawFilter, isComparisonVisible]
  );

  // URL에 없는 탭 값이 들어와도(수동 편집 등) 디폴트 탭으로 떨어뜨린다.
  const tab =
    filter.tab && TABS.includes(filter.tab) ? filter.tab : DEFAULT_TAB;
  const pageNumber = filter.pageNumber ?? DEFAULT_PAGE_NUMBER;
  const pageSize = filter.pageSize ?? DEFAULT_PAGE_SIZE;
  const marketPriceBasis =
    filter.marketPriceBasis &&
    MARKET_PRICE_BASES.includes(filter.marketPriceBasis)
      ? filter.marketPriceBasis
      : DEFAULT_MARKET_PRICE_BASIS;

  // 필터 때문에 0건인지(빈 목록 안내 분기) 판단하는 데 쓴다.
  const hasAppliedFilters =
    BASKET_GROUP_FILTER_KEYS.some((key) => {
      const value = filter[key];
      return Array.isArray(value) ? value.length > 0 : value !== undefined;
    }) || hasPathPickRangeFilter(filter);

  // 정렬 옵션은 탭마다 다르므로 현재 탭에 없는 값이면 그 탭의 디폴트로 떨어뜨린다.
  const sortOptions = BASKET_SORT_OPTIONS_BY_TAB[tab].filter(
    (option) =>
      isComparisonVisible || !BASKET_COMPARISON_SORT_OPTIONS.includes(option)
  );
  const sort =
    filter.sort && sortOptions.includes(filter.sort)
      ? filter.sort
      : sortOptions[0];

  // 기본값은 URL에서 생략해 쿼리스트링을 간결하게 유지한다.
  const applyQuery = useCallback(
    (query?: string) => {
      setFilter((prev) => normalize({ ...prev, query, pageNumber: undefined }));
    },
    [setFilter]
  );

  // 상세 필터 적용·칩 삭제 — 결과가 바뀌므로 첫 페이지로 되돌린다.
  const apply = useCallback(
    (next: PathPickBasketFilter) => {
      setFilter(normalize({ ...next, pageNumber: undefined }));
    },
    [setFilter]
  );

  // 칩 우측 `전체 해제` — 칩으로 노출되는 그룹·범위 필터만 비우고 검색어/탭/정렬은 유지한다.
  const clearFilters = useCallback(() => {
    setFilter((prev) =>
      normalize({ ...prev, ...CLEARED_GROUP_FILTERS, pageNumber: undefined })
    );
  }, [setFilter]);

  const removeValue = useCallback(
    (key: PathPickBasketFilterKey, value: string | number) => {
      // 품절 필터는 단일 선택(스칼라)이라 값 제거 = 미적용(전체)으로 되돌리기다.
      if (key === "soldOut") {
        apply({ ...filter, soldOut: undefined });
        return;
      }
      // 정규화된 filter(숫자)로 비교/삭제해 2개 이상(원래 string[]) 상황에서도 정확히 제거한다.
      const current = (filter[key] ?? []) as (string | number)[];
      apply({
        ...filter,
        [key]: current.filter((item) => item !== value) as any,
      });
    },
    [filter, apply]
  );

  // 탭 전환 시 걸려 있던 필터와 정렬은 초기화한다(탭마다 필터 구성·정렬 기준이 다르다).
  // 검색어는 유지한다 — 탭별로 같은 검색을 이어서 본다.
  const changeTab = useCallback(
    (tab: PathPickBasketTab) => {
      setFilter((prev) =>
        normalize({
          ...prev,
          ...CLEARED_GROUP_FILTERS,
          tab: tab === DEFAULT_TAB ? undefined : tab,
          sort: undefined,
          pageNumber: undefined,
        })
      );
    },
    [setFilter]
  );

  const changeSort = useCallback(
    (sort?: PathPickBasketSortOption) => {
      setFilter((prev) => normalize({ ...prev, sort, pageNumber: undefined }));
    },
    [setFilter]
  );

  // 비교 기준은 마진 정렬일 때만 순서를 바꾸므로(마진값 자체가 이 기준으로 계산된다)
  // 그때만 첫 페이지로 되돌리고, 최근 담은/신청/주문순과 거래순에서는 보던 페이지를 유지한다.
  // (거래순은 비교 표시 게이팅 대상이긴 해도 체결건수/판매량 기준이라 순서가 그대로다)
  // 디폴트 정렬은 URL에서 생략되어 prev.sort가 undefined일 수 있으므로 폴백까지 적용된 sort로 판단한다.
  const changeMarketPriceBasis = useCallback(
    (basis: PathPickMarketPriceBasis) => {
      setFilter((prev) =>
        normalize({
          ...prev,
          marketPriceBasis:
            basis === DEFAULT_MARKET_PRICE_BASIS ? undefined : basis,
          pageNumber: BASKET_MARGIN_SORT_OPTIONS.includes(sort)
            ? undefined
            : prev.pageNumber,
        })
      );
    },
    [setFilter, sort]
  );

  const setPageNumber = useCallback(
    (page: number) => {
      setFilter((prev) =>
        normalize({
          ...prev,
          pageNumber: page === DEFAULT_PAGE_NUMBER ? undefined : page,
        })
      );
    },
    [setFilter]
  );

  const setPageSize = useCallback(
    (size: number) => {
      setFilter((prev) =>
        normalize({
          ...prev,
          pageSize: size === DEFAULT_PAGE_SIZE ? undefined : size,
          pageNumber: undefined,
        })
      );
    },
    [setFilter]
  );

  return {
    filter,
    tab,
    sort,
    sortOptions,
    hasAppliedFilters,
    pageNumber,
    pageSize,
    marketPriceBasis,
    apply,
    applyQuery,
    clearFilters,
    removeValue,
    changeTab,
    changeSort,
    changeMarketPriceBasis,
    setPageNumber,
    setPageSize,
  };
};

export default usePathPickBasketFilter;
